// window.global = window;
import * as Sentry from "@sentry/react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { PersistGate } from "redux-persist/integration/react";
import { polyfillCountryFlagEmojis } from "country-flag-emoji-polyfill";
import App from "./App";
import "./i18n/config";
import "./index.scss";
import { getItemWithExpiry } from "./lib/utility";
import { installTruncatedTitles } from "./lib/truncatedTitles";
import { persistor, store } from "./redux/store";   
import { WebSocketNotificationProvider } from "./contexts/WebSocketNotification";
import { ThemeProvider } from "./contexts/ThemeContext";
import { FullscreenProvider } from "./contexts/FullscreenContext";

// Polyfill country flag emojis for Windows / Chromium browsers where country flag emojis default to two-letter text
polyfillCountryFlagEmojis(
  "Twemoji Country Flags",
  `${(import.meta.env.BASE_URL || "/").replace(/\/$/, "")}/fonts/TwemojiCountryFlags.woff2`
);


const updateFavicon = () => {
  try {
    if (import.meta.env.VITE_REACT_SERVER_CONFIGURATION) {
      const serverconfiguration = JSON.parse(
        import.meta.env.VITE_REACT_SERVER_CONFIGURATION
      );
      if (serverconfiguration?.favicon) {
        const link = document.querySelector("link[rel~='icon']");
        if (link) {
          link.href = serverconfiguration.favicon;
        }
      }
    }
  } catch (error) {
    console.log("Using default favicon from HTML");
  }
};

updateFavicon();
installTruncatedTitles();

const Fallback = ({ error, resetErrorBoundary }) => (
  <div style={{ backgroundColor: "yellow", padding: "10px" }}>
    <h2 style={{ color: "teal" }}>Something went wrong</h2>
    <p style={{ color: "red" }}>Error message: {error.message}</p>
    <button onClick={resetErrorBoundary}>Try again</button>
  </div>
);

const user = (globalThis.profileData =
  getItemWithExpiry("profileData") || null);

Sentry.init({
  dsn: "https://97ffc138f2a69f54a0624cc4b9cc6086@o4508438638886912.ingest.de.sentry.io/4508442313752656",
  environment: "production",

  beforeSend(event, hint) {
    if (hint?.originalException?.message === "Network Error") {
      return null;
    }

    if (hint?.originalException instanceof TypeError) {
      return null;
    }
    if (hint?.originalException instanceof ReferenceError) {
      return null;
    }

    if (event.environment === "development") {
      return null;
    }

    if (event.level === "warning") {
      return null;
    }

    event.tags = {
      ...event.tags,
      customTag: "CustomTagValue",
    };

    event.extra = {
      ...event.extra,
      debugInfo: "Additional debug context",
    };

    event.user = {
      ...event.user,
      id: user?.masterID || "unknown",
      email: user?.email || "unknown@example.com",
      username: user?.userName || "Guest",
    };

    if (event.tags?.feature === "login") {
      return null;
    }

    return event;
  },
});

createRoot(document.getElementById("root")).render(
  <>
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <ThemeProvider>
          <FullscreenProvider>
            <WebSocketNotificationProvider>
              <ToastContainer style={{ zIndex: 100000 }} />
              <Sentry.ErrorBoundary fallback={<Fallback />}>
                <App />
              </Sentry.ErrorBoundary>
            </WebSocketNotificationProvider>
          </FullscreenProvider>
        </ThemeProvider>
      </PersistGate>
    </Provider>
  </>
);
